/**
 * The layout gallery: every arrangement a new map can start with, grouped the way a person looks
 * for one.
 *
 * Shared by the web and the phone, which draw the cards their own way. Each card's picture is a
 * real map — `layoutSample` — arranged by the real layout and drawn by the real renderer, so the
 * gallery cannot promise a shape the editor does not make.
 */
import { createDocument, createNode, orderAtEnd, } from '@orbit-mind/domain';
import { isKnownLayout, layoutFor } from '@orbit-mind/layout-engine';
import { DIRECTIONAL_LAYOUT_FAMILIES, LAYOUT_DIRECTIONS, LAYOUT_FAMILIES, layoutId, } from '@orbit-mind/shared-types';
/**
 * The first row: one of each *kind* of map, rather than six directions of the same one — the
 * choices that look most different from each other, so a person sees the range at a glance.
 */
export const FEATURED_LAYOUTS = [
    { id: 'curved-line-tree/left-right', label: 'Mind map' },
    { id: 'radial', label: 'Radial' },
    { id: 'cornered-tree/down', label: 'Org chart' },
    { id: 'fishbone/right', label: 'Fishbone' },
    { id: 'simple-tree-curved/right', label: 'Sketch' },
    { id: 'cornered-list/down', label: 'Outline' },
    { id: 'cornered-list/right', label: 'Columns' },
    { id: 'direct-line-tree/down', label: 'Hierarchy' },
    { id: 'metro', label: 'Metro map' },
    { id: 'timeline', label: 'Timeline' },
];
/**
 * The structures: diagrams of their own rather than ways of drawing a tree. Each arranges the
 * whole map, and each draws a picture around the nodes — lines, an axis, cells, lanes, a road.
 */
export const STRUCTURE_OPTIONS = [
    { id: 'metro', label: 'Metro map' },
    { id: 'timeline', label: 'Timeline' },
    { id: 'matrix', label: '2×2 matrix' },
    { id: 'board', label: 'Board' },
    { id: 'road', label: 'Road' },
    { id: 'layers', label: 'Layers' },
];
const DIRECTION_LABELS = {
    right: 'Right',
    left: 'Left',
    'left-right': 'Left-Right',
    down: 'Down',
    up: 'Up',
    'up-down': 'Up-Down',
};
/** Every group, in the order the gallery shows them. Only layouts this build can run. */
export function layoutGallery() {
    const groups = [
        { key: 'featured', label: 'Featured', options: FEATURED_LAYOUTS },
        { key: 'structures', label: 'Structures', options: STRUCTURE_OPTIONS },
        {
            key: 'free',
            label: 'Free-form and special shapes',
            options: [
                { id: 'free-form', label: 'Free-form' },
                { id: 'radial', label: 'Radial' },
                { id: 'fishbone/right', label: 'Fishbone' },
            ],
        },
    ];
    for (const family of DIRECTIONAL_LAYOUT_FAMILIES) {
        if (family === 'fishbone')
            continue;
        const options = LAYOUT_DIRECTIONS.map((direction) => ({
            id: layoutId(family, direction),
            label: DIRECTION_LABELS[direction],
        })).filter((option) => isKnownLayout(option.id));
        if (options.length > 0) {
            groups.push({ key: family, label: LAYOUT_FAMILIES[family].label, options });
        }
    }
    return groups;
}
/**
 * What a layout is called, the way the gallery names it: the featured name when it has one, and
 * otherwise its family and direction — "Continuous list, Down".
 *
 * One answer for every screen that has to say which layout a map is in, so the Builder, the AI
 * tab and the editor's menu cannot call one layout three things.
 */
export function layoutLabel(id) {
    const featured = FEATURED_LAYOUTS.find((option) => option.id === id);
    if (featured !== undefined)
        return featured.label;
    for (const group of layoutGallery()) {
        const option = group.options.find((candidate) => candidate.id === id);
        if (option !== undefined) {
            return group.key === 'free' || group.key === 'structures'
                ? option.label
                : `${group.label}, ${option.label}`;
        }
    }
    return id;
}
const SAMPLE_ID = 'map_layout_sample';
const SAMPLE_ROOT = 'node_sample_root';
const SAMPLE_AUTHOR = 'user_sample';
const SAMPLE_TIME = 0;
/** Node sizes by depth: a box that reads as "a centre", "a branch" and "a leaf" at card size. */
const SAMPLE_SIZES = [
    { width: 104, height: 40 },
    { width: 72, height: 28 },
    { width: 52, height: 22 },
];
/**
 * Where free-form's nodes sit, by hand, since free-form is the one layout that places nothing.
 *
 * Deliberately a little uneven — no two branches the same distance out — because a tidy ring
 * would be the radial card again, and the point of this one is that a person put things there.
 */
const FREE_FORM_POSITIONS = {
    node_sample_0: { x: -150, y: -92 },
    node_sample_0_a: { x: -262, y: -150 },
    node_sample_0_b: { x: -276, y: -62 },
    node_sample_1: { x: 168, y: -70 },
    node_sample_1_a: { x: 288, y: -128 },
    node_sample_1_b: { x: 300, y: -38 },
    node_sample_2: { x: 190, y: 92 },
    node_sample_2_a: { x: 302, y: 64 },
    node_sample_2_b: { x: 276, y: 152 },
    node_sample_3: { x: -168, y: 82 },
    node_sample_3_a: { x: -282, y: 44 },
    node_sample_3_b: { x: -246, y: 140 },
};
/**
 * The map every gallery card draws: a centre, four branches, two leaves on each.
 *
 * Wordless, because a card is a thumbnail and words at that size are noise; the boxes, the
 * underlines and the lines between them are the whole of what tells two layouts apart. Sized
 * rather than measured for the same reason — an empty node measures as the smallest box there
 * is, and a map of identical small squares hides the difference between a centre and a leaf.
 */
export function layoutSample(id) {
    const document = createDocument({
        mapId: SAMPLE_ID,
        rootNodeId: SAMPLE_ROOT,
        workspaceId: 'workspace_sample',
        ownerId: SAMPLE_AUTHOR,
        now: SAMPLE_TIME,
        title: '',
        rootText: '',
        layoutId: id,
    });
    const freeForm = id === 'free-form';
    const nodes = {};
    for (const [nodeId, node] of Object.entries(document.nodes)) {
        nodes[nodeId] = { ...node, ...SAMPLE_SIZES[0] };
    }
    let order = 0;
    const add = (nodeId, parentId, depth) => {
        order += 1;
        const position = freeForm ? FREE_FORM_POSITIONS[nodeId] : undefined;
        nodes[nodeId] = {
            ...createNode({
                id: nodeId,
                mapId: SAMPLE_ID,
                parentId: parentId,
                order: orderAtEnd([]),
                now: SAMPLE_TIME,
                by: SAMPLE_AUTHOR,
                ...(position === undefined ? {} : { position }),
            }),
            ...SAMPLE_SIZES[depth],
            order: `a${String(order).padStart(3, '0')}`,
        };
    };
    for (let branch = 0; branch < 4; branch += 1) {
        const branchId = `node_sample_${String(branch)}`;
        add(branchId, SAMPLE_ROOT, 1);
        add(`${branchId}_a`, branchId, 2);
        add(`${branchId}_b`, branchId, 2);
    }
    return { ...document, nodes };
}
/**
 * The sample, already arranged by its layout.
 *
 * For a client that draws a document as it is rather than arranging it first — the phone's
 * thumbnails. Every sample node is sized rather than measured, so arranging needs no font and no
 * canvas, and gives the same picture on every device.
 */
export function arrangedLayoutSample(id) {
    const sample = layoutSample(id);
    const { positions } = layoutFor(id).run({
        document: sample,
        extentOf: (nodeId) => {
            const node = sample.nodes[nodeId];
            return { width: node?.width ?? 40, height: node?.height ?? 28 };
        },
        // Nothing in a sample was placed by hand except free-form's, which free-form keeps anyway.
        preserveManualPositions: false,
    });
    const nodes = {};
    for (const [nodeId, node] of Object.entries(sample.nodes)) {
        const placed = positions[nodeId];
        nodes[nodeId] = placed === undefined ? node : { ...node, x: placed.x, y: placed.y };
    }
    return { ...sample, nodes };
}
//# sourceMappingURL=gallery.js.map